'use client'

import { useCallback, useRef } from 'react'

/**
 * Restores scrolling for a container rendered inside a Radix Popover
 * Portal that lives inside a Radix Dialog (or any other primitive that
 * uses `react-remove-scroll`).
 *
 * **Why this exists.** Radix Dialog wraps its content with
 * `react-remove-scroll`, which installs a **bubble-phase, non-passive**
 * listener on `document` for `wheel` and `touchmove` and calls
 * `preventDefault()` on events whose target sits outside its lock
 * region (it whitelists the dialog's own content via `shards` — but
 * only that). Anything portaled to `<body>` to escape the dialog's
 * `overflow:hidden` + transform therefore loses native scrolling.
 *
 * **Why a previous version of this hook failed.** Because
 * `react-remove-scroll`'s listener is in the **bubble** phase at
 * `document`, gating on `event.defaultPrevented` in our element-level
 * bubble handler doesn't work — our handler runs *before* the document
 * handler, so `defaultPrevented` is still `false` at that point. By
 * the time the document handler calls `preventDefault()`, our handler
 * has already returned without doing anything.
 *
 * **The fix.** This hook returns a **callback ref** that attaches a
 * **native** non-passive `wheel` listener directly to the scrollable
 * element. It always calls `preventDefault()` and manually adjusts
 * `scrollTop` / `scrollLeft` itself. That:
 *
 * 1. Blocks the browser's native scroll (which would either be killed
 *    by RemoveScroll inside a dialog anyway, or allowed outside one —
 *    we don't depend on which).
 * 2. Manually delivers the scroll, so behavior is consistent in both
 *    contexts.
 * 3. Means RemoveScroll's later `preventDefault()` is a harmless no-op.
 *
 * Native listeners are used (rather than React's synthetic `onWheel`)
 * because React's event delegation is anchored at the React root
 * container — and content portaled to `<body>` is *not* a descendant
 * of that container, so synthetic wheel events can fail to fire
 * reliably for portaled content.
 *
 * Scrollbar dragging keeps working without help — `react-remove-scroll`
 * does not intercept `mousedown`.
 *
 * Trade-off: the manual scroll loses native momentum / inertia, which
 * is a worthwhile cost for working consistently in and out of dialogs.
 *
 * @example
 *   const portalScrollRef = useScrollableInPortal<HTMLDivElement>()
 *   <PopoverContent>
 *     <div ref={portalScrollRef} className="max-h-60 overflow-y-auto">
 *       {items}
 *     </div>
 *   </PopoverContent>
 */
export function useScrollableInPortal<T extends HTMLElement = HTMLDivElement>() {
  const cleanupRef = useRef<(() => void) | null>(null)

  return useCallback((node: T | null) => {
    // Tear down listeners from a previous node (or unmount).
    cleanupRef.current?.()
    cleanupRef.current = null
    if (!node) return

    let touchY: number | null = null
    let touchX: number | null = null

    const onWheel = (event: WheelEvent) => {
      let dy = event.deltaY
      let dx = event.deltaX
      // Normalize delta units. Most modern browsers use pixel mode (0);
      // line and page modes need to be converted so the manual scroll
      // distance feels like the native one.
      if (event.deltaMode === 1) {
        dy *= 16
        dx *= 16
      } else if (event.deltaMode === 2) {
        dy *= node.clientHeight
        dx *= node.clientWidth
      }
      if (event.cancelable) event.preventDefault()
      node.scrollTop += dy
      node.scrollLeft += dx
    }

    const onTouchStart = (event: TouchEvent) => {
      const t = event.touches[0]
      touchY = t?.clientY ?? null
      touchX = t?.clientX ?? null
    }

    const onTouchMove = (event: TouchEvent) => {
      const t = event.touches[0]
      if (!t) return
      if (event.cancelable) event.preventDefault()
      if (touchY != null) {
        node.scrollTop += touchY - t.clientY
        touchY = t.clientY
      }
      if (touchX != null) {
        node.scrollLeft += touchX - t.clientX
        touchX = t.clientX
      }
    }

    // `passive: false` is required so `preventDefault()` is honored.
    // `touchstart` stays passive — we don't need to prevent the touch
    // start itself, only the subsequent `touchmove`.
    node.addEventListener('wheel', onWheel, { passive: false })
    node.addEventListener('touchstart', onTouchStart, { passive: true })
    node.addEventListener('touchmove', onTouchMove, { passive: false })

    cleanupRef.current = () => {
      node.removeEventListener('wheel', onWheel)
      node.removeEventListener('touchstart', onTouchStart)
      node.removeEventListener('touchmove', onTouchMove)
    }
  }, [])
}
